import React, { useState, useEffect, useRef, useCallback } from 'react';
import { shouldAvoidWebGPUInThisEnvironment } from '../workers/webGpuPolicy';

export interface TextToSQLProps {
  tableName: string;
  columns: string[];
  onSQLGenerated: (sql: string) => void;
  disabled?: boolean;
}

type ModelStatus = 'idle' | 'loading' | 'ready' | 'generating' | 'error';

interface WorkerMessage {
  type: 'progress' | 'loaded' | 'token' | 'done' | 'error';
  message?: string;
  token?: string;
  sql?: string;
  modelName?: string;
  runtime?: 'webgpu' | 'wasm';
}

const STATUS_LABEL: Record<ModelStatus, string> = {
  idle: '[NOT LOADED]',
  loading: '[LOADING...]',
  ready: '[READY]',
  generating: '[GENERATING...]',
  error: '[ERROR]',
};

const STATUS_COLOR: Record<ModelStatus, string> = {
  idle: 'text-gray-400',
  loading: 'text-yellow-400',
  ready: 'text-green-400',
  generating: 'text-cyan-400',
  error: 'text-red-400',
};

function detectWebGPU(): boolean {
  // Mirror the worker's logic: Android Chrome exposes navigator.gpu but WebGPU is
  // unreliable there, so the worker always falls back to WASM on Android.
  if (shouldAvoidWebGPUInThisEnvironment()) return false;
  return typeof navigator !== 'undefined' && 'gpu' in navigator && navigator.gpu !== null;
}

const TextToSQL: React.FC<TextToSQLProps> = ({
  tableName,
  columns,
  onSQLGenerated,
  disabled = false,
}) => {
  const [naturalQuery, setNaturalQuery] = useState('');
  const [generatedSQL, setGeneratedSQL] = useState('');
  const [modelStatus, setModelStatus] = useState<ModelStatus>('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [webGPUAvailable, setWebGPUAvailable] = useState(() => detectWebGPU());
  const [activeModelName, setActiveModelName] = useState(() =>
    detectWebGPU() ? 'Bonsai 1.7B' : 'T5-small text-to-SQL'
  );
  const workerRef = useRef<Worker | null>(null);

  useEffect(() => {
    const worker = new Worker(
      new URL('../workers/textToSql.worker.ts', import.meta.url),
      { type: 'module' }
    );

    worker.onmessage = (event: MessageEvent<WorkerMessage>) => {
      const { type, message, token, sql } = event.data;
      switch (type) {
        case 'progress':
          // Keep 'generating' label when we're mid-generation but the model
          // is still loading for the first time.
          setStatusMessage(message ?? '');
          setModelStatus((prev) => (prev === 'generating' ? 'generating' : 'loading'));
          break;
        case 'loaded':
          setModelStatus('ready');
          setStatusMessage('Model ready');
          if (event.data.modelName) setActiveModelName(event.data.modelName);
          if (event.data.runtime) setWebGPUAvailable(event.data.runtime === 'webgpu');
          break;
        case 'token':
          if (token !== undefined) {
            setModelStatus('generating');
            setGeneratedSQL((prev) => prev + token);
          }
          break;
        case 'done':
          setGeneratedSQL(sql ?? '');
          setModelStatus('ready');
          setStatusMessage('SQL generated');
          break;
        case 'error':
          setError(message ?? 'Unknown error');
          setStatusMessage(message ?? '');
          setModelStatus('error');
          break;
      }
    };

    worker.onerror = (e) => {
      setError(`Worker crashed: ${e.message ?? 'unknown error'}`);
      setModelStatus('error');
    };

    worker.onmessageerror = () => {
      setError('Worker message serialisation error');
      setModelStatus('error');
    };

    workerRef.current = worker;
    return () => worker.terminate();
  }, []);

  const handlePreload = useCallback(() => {
    if (!workerRef.current || modelStatus !== 'idle') return;
    setModelStatus('loading');
    setStatusMessage('Initialising model...');
    workerRef.current.postMessage({ type: 'load' });
  }, [modelStatus]);

  const handleGenerate = useCallback(() => {
    if (!naturalQuery.trim() || !workerRef.current || disabled) return;
    if (modelStatus === 'generating' || modelStatus === 'loading') return;

    setGeneratedSQL('');
    setError(null);
    setModelStatus('generating');
    setStatusMessage('Generating SQL...');

    workerRef.current.postMessage({
      type: 'generate',
      query: naturalQuery.trim(),
      tableName,
      columns,
    });
  }, [naturalQuery, tableName, columns, disabled, modelStatus]);

  const handleUseSQL = useCallback(() => {
    if (generatedSQL.trim()) {
      onSQLGenerated(generatedSQL.trim());
    }
  }, [generatedSQL, onSQLGenerated]);

  const isGenerateDisabled =
    disabled ||
    !naturalQuery.trim() ||
    modelStatus === 'generating' ||
    modelStatus === 'loading';

  return (
    <div className="w-full max-w-5xl mx-auto space-y-6">
      {/* Status panel */}
      <div className="neon-panel p-6">
        <div className="neon-panel-header">AI Text-to-SQL Engine</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 font-mono text-sm">
          <div className="text-center">
            <div className="text-gray-400 mb-2">Model:</div>
            <div className="text-blue-400">{activeModelName}</div>
          </div>
          <div className="text-center">
            <div className="text-gray-400 mb-2">Runtime:</div>
            <div className={webGPUAvailable ? 'text-purple-400' : 'text-amber-400'}>
              {webGPUAvailable ? 'WebGPU' : 'WASM fallback'}
            </div>
          </div>
          <div className="text-center">
            <div className="text-gray-400 mb-2">Status:</div>
            <div className={`text-lg ${STATUS_COLOR[modelStatus]}`}>
              {STATUS_LABEL[modelStatus]}
            </div>
          </div>
          <div className="text-center">
            <div className="text-gray-400 mb-2">Table:</div>
            <div className="neon-glow-text truncate">{tableName}</div>
          </div>
        </div>
        {statusMessage && (
          <div className={`mt-4 text-center font-mono text-sm ${
            modelStatus === 'error' ? 'text-red-400' : 'text-yellow-300 animate-pulse'
          }`}>
            {statusMessage}
          </div>
        )}
      </div>

      {/* No WebGPU: worker falls back to ONNX Runtime WASM (slower, same privacy) */}
      {!webGPUAvailable && (
        <div className="neon-panel p-4 font-mono text-sm text-center text-amber-300 border border-amber-500/30">
          <strong>No WebGPU</strong> — Inference will use the WASM backend (slower). For faster runs,
          use Chrome 113+ or Edge 113+ with WebGPU enabled.
        </div>
      )}

      {/* Natural language input */}
      <div className="neon-panel p-6">
        <div className="neon-panel-header">Natural Language Query</div>
        <div className="space-y-4">
          <div className="flex items-start gap-2">
            <span className="mt-3 font-mono text-cyan-400 text-lg shrink-0">&gt;</span>
            <textarea
              value={naturalQuery}
              onChange={(e) => setNaturalQuery(e.target.value)}
              placeholder={`e.g. "Show all rows where age > 30 ordered by name"`}
              disabled={disabled || modelStatus === 'generating'}
              rows={3}
              className="neon-input w-full p-3 resize-none text-lg leading-snug"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.ctrlKey) handleGenerate();
              }}
            />
          </div>

          <div className="flex flex-wrap gap-4 justify-center">
            {modelStatus === 'idle' && (
              <button
                onClick={handlePreload}
                className="neon-button neon-button-blue px-6 py-3 text-sm"
              >
                Preload Model
              </button>
            )}
            <button
              onClick={handleGenerate}
              disabled={isGenerateDisabled}
              className={`neon-button px-8 py-3 text-sm ${
                isGenerateDisabled ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {modelStatus === 'generating' ? 'Generating...' : 'Generate SQL'}
            </button>
          </div>

          <p className="text-center text-gray-600 font-mono text-xs">
            Ctrl+Enter to generate • Runs 100% locally (WebGPU or WASM)
            {(modelStatus === 'generating' || modelStatus === 'loading') && statusMessage.includes('shader') && (
              <span className="block mt-1 text-yellow-500">
                First run compiles GPU shaders — this can take 30–90 seconds, please wait.
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Generated SQL output */}
      {(generatedSQL || modelStatus === 'generating') && (
        <div className="neon-panel p-6">
          <div className="neon-panel-header">Generated SQL</div>
          <div className="space-y-4">
            <pre className="neon-input p-4 text-green-400 font-mono text-base whitespace-pre-wrap min-h-[80px] overflow-auto leading-relaxed">
              {generatedSQL || (
                <span className="animate-pulse text-cyan-400">_</span>
              )}
            </pre>
            {generatedSQL && modelStatus !== 'generating' && (
              <div className="flex justify-center">
                <button
                  onClick={handleUseSQL}
                  className="neon-button neon-button-blue px-8 py-3 text-sm"
                >
                  Use This SQL
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="neon-error p-4 font-mono text-sm">
          <strong>ERROR:</strong> {error}
        </div>
      )}
    </div>
  );
};

export default TextToSQL;
